<script setup>
import { ref, computed, watch, provide, onBeforeMount, onBeforeUnmount } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'

import StreamService from '@/services/StreamService'
import AudienceService from '@/services/AudienceService'
import { useAudienceRoom } from '@/composables/useAudienceRoom'
import { AUDIENCE_CONSOLE, FEATURES } from '@/components/audience/audienceConsole'

import Spinner from '@/components/ui/Spinner.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import { useChatOnlyLock } from '@/composables/useChatOnlyLock'

// The shell of the Audience tab.
//
// The tab outgrew a single page of stacked cards, so its faces live in three
// route children — Live room, Panel setup, Email capture — while everything
// long-lived stays here: the settings block and its persist cycle, the room
// socket, the stats poll, the captures count and the CTA state. The socket in
// particular must not live in the Live room pane: a trip to Panel setup would
// unmount it, dropping the connection, presence and every badge, and force a
// rejoin on the way back. Children take what they need through
// AUDIENCE_CONSOLE and keep pane-local state (modals, drafts) to themselves.
//
// The control bar up top never disappears — whatever state the feature is in,
// the master switch and the provider selector are the way out of it.

const { notify } = useNotification()

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id')
		}
	},
	streamAlive: {
		type: Boolean
	}
})

// Panel setup, Email capture and Contacts are configuration and contact-data
// surfaces, not moderation — a chat-only moderator reaches the Live room only.
// Declared up here because `refreshCapturesTotal` below reads it.
const { locked: chatOnlyLocked, tooltip: chatOnlyTooltip, isLockedRoute } = useChatOnlyLock(
	computed(() => props.stream?.type === 'live'),
)

// Mirrors the `audience` block in the stream's settings, which the embed player
// reads to decide whether to render the panel and which features to allow.
const DEFAULT_SETTINGS = {
	enabled: false,
	provider: 'castr',
	chat: true,
	qa: false,
	polls: true,
	reactions: true,
	slowModeSec: 0,
	anonymousAllowed: true,
	// Cosmetic, not a panel feature: deliberately not in FEATURES, so toggling
	// it neither forces a room rejoin nor counts toward "panel will not appear".
	countryFlags: false,
	// Whether the player opens the chat panel automatically on load. On by
	// default; also not a FEATURES entry (toggling it needs no room rejoin).
	openOnLoad: true,
	// Persisted whole like `theme`. Off by default: asking viewers for
	// personal data is opt-in. A blank privacyUrl means the viewer form links
	// to Castr's standard privacy policy.
	emailCapture: {
		mode: 'off',
		collectName: false,
		title: '',
		nameLabel: '',
		emailLabel: '',
		buttonText: '',
		consentText: '',
		privacyUrl: null
	}
}

// Absent on any stream saved before the panel could be themed. `dark` is what
// those streams already look like, so defaulting to it changes nothing for them.
const DEFAULT_THEME = {
	preset: 'dark',
	background: null,
	surface: null,
	text: null,
	accent: null
}

const loading = ref(true)
const settings = ref({ ...DEFAULT_SETTINGS })

// Pending state is scoped to the control that started the write: a debounced
// theme save must not animate the slow-mode Save button two rows up. Counters
// rather than booleans, because a second save for the same key can start
// while the first is still queued. The derived global flag remains for the
// control bar's emergency switches, which genuinely should lock during any
// audience-block write.
const savingCounts = ref({})

const beginSaving = (source) => {
	savingCounts.value = {
		...savingCounts.value,
		[source]: (savingCounts.value[source] || 0) + 1
	}
}

const endSaving = (source) => {
	const next = { ...savingCounts.value }
	const count = (next[source] || 0) - 1
	if (count <= 0) delete next[source]
	else next[source] = count
	savingCounts.value = next
}

const isSaving = (source) => (savingCounts.value[source] || 0) > 0

const saving = computed(() => Object.keys(savingCounts.value).length > 0)

// Drives the panel accent unless the theme overrides it, so the theme picker
// has to know about it to describe the accent honestly.
const embedThemeColor = ref('')

const stats = ref(null)

// Per-room content filters. These live in the room rather than the stream's
// settings, so they are read back from the stats snapshot and written with a
// partial patch. Null until a snapshot has landed, which is what tells the
// switches apart from a genuine "both off".
const roomConfig = ref(null)
const filterSaving = ref('')

// How many viewers have left an email, for the badge and the look-up
// affordances. Null until the first answer arrives, so a backend that has not
// shipped the endpoint yet shows nothing rather than a lying zero.
const capturesTotal = ref(null)

// The CTA as the last stats snapshot had it — the fallback for when the
// socket is not connected. The socket's copy wins while live because it moves
// on the broadcast itself instead of the next poll.
const ctaFromStats = ref(null)

const {
	status: roomStatus,
	error: roomError,
	isLive,
	me,
	canSendChat,
	canSendQuestion,
	sendError,
	presence,
	messages,
	sortedQuestions,
	polls,
	pinned,
	cta: roomCta,
	reactionCounts,
	connect: connectRoom,
	disconnect: disconnectRoom,
	purgeAndRejoin,
	sendChat,
	sendQuestion
} = useAudienceRoom(props.stream._id)

const usingCastr = computed(() => settings.value.enabled && settings.value.provider === 'castr')

const banCount = computed(() => (stats.value && stats.value.bans) || 0)

const theme = computed(() => ({ ...DEFAULT_THEME, ...(settings.value.theme || {}) }))

const emailCaptureOn = computed(() => {
	const block = settings.value.emailCapture
	return !!block && block.mode && block.mode !== 'off'
})

// Socket copy while connected — it moves the moment a `cta.set` broadcast
// lands. The stats snapshot covers the tab before it joins the room.
const activeCta = computed(() => (isLive.value ? roomCta.value : ctaFromStats.value))

const ctaClicks = computed(() => {
	if (!stats.value) return null
	return _.isFinite(stats.value.ctaClicks) ? stats.value.ctaClicks : 0
})

// What the sub-tabs owe a host's attention while they are elsewhere:
// questions waiting on an answer and polls currently taking votes.
const unansweredCount = computed(() => sortedQuestions.value.filter((q) => !q.answered).length)
const livePollCount = computed(() => polls.value.filter((p) => !p.closedTs).length)

// The legacy chat systems (the player-functions `chatEnabled` and the `cbox`
// provider) are consolidated onto the Castr chat by the API: reads come back
// already reconciled, and saving the audience block retires the old `chatEnabled`
// flag. So the dashboard just drives `audience.enabled` as the single control.
onBeforeMount(async () => {
	try {
		const metadata = await StreamService.getStreamMetadata(props.stream._id)
		settings.value = { ...DEFAULT_SETTINGS, ..._.get(metadata, 'audience', {}) }
		embedThemeColor.value = _.get(metadata, 'embedThemeColor', '') || ''
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not load chat settings' })
	}
	loading.value = false

	if (usingCastr.value) {
		connectRoom()
		// Independent of the socket: the filters read their current values
		// out of this, and they should be answerable even if the room cannot
		// be joined.
		refreshStats()
		refreshCapturesTotal()
	}
})

// Switching the feature off or the provider away should drop the socket
// rather than leave it reconnecting in the background.
watch(usingCastr, (active) => {
	if (active) connectRoom()
	else disconnectRoom()
})

// Tokens carry the features the stream had when they were minted, so switching
// one on mid-session leaves the live socket unable to use it — the composer for
// a freshly enabled Q&A would sit there refusing to post. Rejoin to pick the
// new set up. Only while we are already in the room: toggling features from a
// disconnected tab should not drag us into one.
watch(() => FEATURES.map((f) => settings.value[f.key]).join(','), () => {
	if (usingCastr.value && roomStatus.value !== 'idle') connectRoom()
})

/**
 * The write itself. Never throws — a failure notifies and reports false.
 */
const writeSettings = async (patch) => {
	try {
		// Read-modify-write against fresh server state, not this tab's copy. The
		// block is saved whole, so persisting from a tab that loaded before some
		// other tab changed a flag would silently revert that flag — a stale
		// `anonymousAllowed: false` coming back from the dead and hiding the
		// panel for every viewer is how this was found.
		const metadata = await StreamService.getStreamMetadata(props.stream._id)
		const fresh = { ...DEFAULT_SETTINGS, ..._.get(metadata, 'audience', {}) }
		const next = { ...fresh, ...patch }

		await StreamService.saveStreamMetadata(props.stream._id, 'audience', next)
		StreamService.clearStreamMetadataCache(props.stream._id, props.stream.key)
		settings.value = next
		return true
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not save audience settings' })
		return false
	}
}

// Writes run one at a time. Overlap was possible before this queue — the
// theme save is debounced and fire-and-forget, so it could interleave with a
// toggle's read-modify-write and the later read would clobber the earlier
// write. The global saving flag masked most of that by accident; now that
// pending state is per-control, the queue makes it correct on purpose.
let persistQueue = Promise.resolve()

/**
 * Persists the whole audience block. The backend whitelists it as one top-level
 * settings key, so sub-flags are saved together rather than one call each.
 *
 * @param {object} patch
 * @param {string} source what initiated the write — the settings key or a
 *   section name — so only that control shows the pending state.
 */
const persist = async (patch = {}, source = 'settings') => {
	beginSaving(source)

	const run = persistQueue.then(() => writeSettings(patch))
	persistQueue = run

	try {
		return await run
	} finally {
		endSaving(source)
	}
}

const onToggle = async (key) => {
	// v-model has already flipped the local value; persist what is on screen and
	// roll it back if the request fails, so the switch never lies.
	const value = settings.value[key]
	const ok = await persist({ [key]: value }, key)
	if (!ok) settings.value[key] = !value
}

/* ------------------------------- theme -------------------------------- */

// Colour inputs fire continuously while a swatch is dragged, so the writes are
// held back; the preview is driven from local state and updates on every frame
// regardless. A preset is a single deliberate click and is saved at once.
const saveTheme = _.debounce((next) => persist({ theme: next }, 'theme'), 600)

const onThemeUpdate = (next) => {
	const immediate = next.preset !== theme.value.preset
	settings.value = { ...settings.value, theme: next }

	if (immediate) {
		saveTheme.cancel()
		persist({ theme: next }, 'theme')
		return
	}

	saveTheme(next)
}

onBeforeUnmount(() => {
	// A colour changed a moment before leaving the tab is still owed a write.
	saveTheme.flush()
	if (statsTimer) window.clearInterval(statsTimer)
})

/* ---------------------------- stats & polls ---------------------------- */

const refreshStats = async (silent = false) => {
	try {
		stats.value = await AudienceService.getStats(props.stream._id)
		if (stats.value && stats.value.config) roomConfig.value = { ...stats.value.config }
		if (stats.value) ctaFromStats.value = stats.value.cta || null
	} catch (err) {
		// The poller below retries on its own; only event-driven refreshes
		// (join, ban, clear) are worth interrupting anyone over.
		if (silent !== true) notify({ group: 'error', text: err.message || 'Could not load room stats' })
	}
}

// Stats are a snapshot from the admin API rather than something the socket
// pushes, so pull one on every join — first connect, after a drop, after a
// purge — and then keep it honest on a slow poll while the room is live.
let statsTimer = null

watch(isLive, (live) => {
	if (live) {
		refreshStats()
		if (!statsTimer) {
			statsTimer = window.setInterval(() => {
				refreshStats(true)
				// The captures count ages the same way the stat chips did, so
				// it rides the same poll — but only while it is collecting.
				if (emailCaptureOn.value) refreshCapturesTotal()
			}, 30000)
		}
	} else if (statsTimer) {
		window.clearInterval(statsTimer)
		statsTimer = null
	}
})

/**
 * Just the total: one row is the cheapest question the captures endpoint
 * answers. Always silent — a red toast about a count on every tab load helps
 * nobody.
 */
const refreshCapturesTotal = async () => {
	// The Contacts surface is denied outright for a chat-only moderator, so polling
	// it would only produce a 403 every 30 seconds behind a silent catch.
	if (chatOnlyLocked.value) return
	try {
		const res = await AudienceService.listCaptures(props.stream._id, { limit: 1, skip: 0 })
		capturesTotal.value = (res && res.total) || 0
	} catch (err) {
		// Left at its last known value.
	}
}

/**
 * @param {'profanityFilter'|'blockLinks'} key
 * @param {boolean} value
 */
const setFilter = async (key, value) => {
	if (!roomConfig.value) return

	const previous = roomConfig.value[key]
	roomConfig.value = { ...roomConfig.value, [key]: value }
	filterSaving.value = key

	try {
		await AudienceService.updateRoomConfig(props.stream._id, { [key]: value })
		if (stats.value && stats.value.config) stats.value.config[key] = value
	} catch (err) {
		roomConfig.value = { ...roomConfig.value, [key]: previous }
		notify({ group: 'error', text: err.message || 'Could not update the room filters' })
	}

	filterSaving.value = ''
}

const onEmailCaptureSave = async (block) => {
	const ok = await persist({ emailCapture: block }, 'emailCapture')
	if (!ok) return

	notify({ group: 'success', text: 'Email capture settings saved' })
	// Switching it on makes the count worth having; switching it off does not
	// make the existing one wrong.
	if (emailCaptureOn.value) refreshCapturesTotal()
}

/* ------------------------------- CTA ---------------------------------- */

// The socket hears its own `cta.set` broadcast while connected; these keep
// the fallback copy honest for a tab that is not in the room, using the
// response the endpoints already return.
const onCtaSet = (cta) => {
	ctaFromStats.value = cta || null
	refreshStats(true)
}

const onCtaRemoved = () => {
	ctaFromStats.value = null
	refreshStats(true)
}

/* --------------------------- shared console ---------------------------- */

provide(AUDIENCE_CONSOLE, {
	// Settings and their persist cycle.
	settings,
	saving,
	isSaving,
	persist,
	onToggle,
	usingCastr,
	emailCaptureOn,
	theme,
	embedThemeColor,
	onThemeUpdate,
	onEmailCaptureSave,

	// The room socket. Lives here so it survives sub-tab switches.
	roomStatus,
	roomError,
	isLive,
	me,
	canSendChat,
	canSendQuestion,
	sendError,
	presence,
	messages,
	sortedQuestions,
	polls,
	pinned,
	reactionCounts,
	unansweredCount,
	livePollCount,
	connectRoom,
	purgeAndRejoin,
	sendChat,
	sendQuestion,

	// Stats snapshot, room filters, captures, CTA.
	stats,
	roomConfig,
	filterSaving,
	setFilter,
	refreshStats,
	banCount,
	capturesTotal,
	refreshCapturesTotal,
	activeCta,
	ctaClicks,
	onCtaSet,
	onCtaRemoved
})

/* ------------------------------ sub-nav -------------------------------- */

const subTabs = computed(() => [
	{
		route: 'LivestreamManageAudienceLiveRoom',
		label: 'Live room',
		count: unansweredCount.value,
		countLabel: `${unansweredCount.value} unanswered question${unansweredCount.value === 1 ? '' : 's'}`,
		// Kept apart from the count on purpose: one is a backlog to work
		// through, the other is a state to know about.
		dot: livePollCount.value > 0,
		dotLabel: 'A poll is live'
	},
	{
		route: 'LivestreamManageAudiencePanelSetup',
		label: 'Panel setup',
		count: 0,
		dot: false
	},
	{
		route: 'LivestreamManageAudienceEmailCapture',
		label: 'Email capture',
		count: 0,
		dot: false
	},
	{
		route: 'LivestreamManageAudienceContacts',
		label: 'Contacts',
		// Records outlive capture being switched off, and someone owed a
		// GDPR erase should still see there is something here.
		count: capturesTotal.value || 0,
		countLabel: `${capturesTotal.value} contact${capturesTotal.value === 1 ? '' : 's'}`,
		dot: false
	}
])
</script>

<template>
	<div>
		<Spinner v-if="loading" class="my-10" />

		<template v-else>
			<!-- The master switch. Whatever is wrong — panel showing when it
			should not — the way out is here, on every sub-tab and in every state. -->
			<section class="bg-surface-1 rounded-xl p-4 md:p-5 mb-4">
				<div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
					<div>
						<h3 class="text-xl font-medium mb-0.5">Chat</h3>
						<p class="text-tiny text-surface-7 mb-0">Show a live chat box alongside the player so viewers can join the conversation.</p>
					</div>

					<div class="flex flex-wrap items-center gap-x-4 gap-y-2">
						<Tooltip :active="chatOnlyLocked" :content="chatOnlyTooltip" :wrap-trigger="chatOnlyLocked">
							<FormSwitch v-model="settings.enabled" :disabled="saving || chatOnlyLocked"
								@change="onToggle('enabled')" />
						</Tooltip>
					</div>
				</div>
			</section>

			<nav class="inline-flex flex-wrap items-center gap-1 bg-surface-1 rounded-lg p-1 mb-4"
				aria-label="Chat sections">
				<Tooltip v-for="tab in subTabs" :key="tab.route"
					:active="isLockedRoute(tab.route)" :content="chatOnlyTooltip"
					wrapper-class="inline-flex" wrap-trigger>
					<!-- Keeps its real `to` when locked: `{}` resolves to the current route,
					     which would give every locked tab `active-class`. Not clickable, not
					     focusable, and the router guard is the backstop. -->
					<router-link :to="{ name: tab.route }"
						class="inline-flex items-center text-sm font-medium text-surface-8 hover:text-white hover:no-underline px-3 py-1.5 rounded-md transition-colors"
						:class="isLockedRoute(tab.route) ? 'opacity-50 pointer-events-none' : null"
						:tabindex="isLockedRoute(tab.route) ? -1 : undefined"
						:aria-disabled="isLockedRoute(tab.route) || undefined"
						active-class="!bg-surface-4 !text-white shadow-sm">
						{{ tab.label }}
						<span v-if="tab.count" :title="tab.countLabel"
							class="ml-1.5 inline-flex items-center justify-center min-w-[1.1rem] rounded-full bg-surface-2 text-main text-tiny leading-4 px-1">
							{{ tab.count }}<span class="sr-only"> — {{ tab.countLabel }}</span>
						</span>
						<span v-if="tab.dot" :title="tab.dotLabel"
							class="ml-1.5 inline-flex items-center">
							<span class="w-1.5 h-1.5 rounded-full bg-success" aria-hidden="true"></span>
							<span class="sr-only">{{ tab.dotLabel }}</span>
						</span>
					</router-link>
				</Tooltip>
			</nav>

			<!-- keep-alive is what lets a half-written email capture form
			survive a detour through another sub-tab: panes deactivate instead
			of unmounting, so drafts and scroll positions hold. -->
			<router-view v-slot="{ Component }">
				<keep-alive>
					<component :is="Component" :stream="stream" :stream-alive="streamAlive" />
				</keep-alive>
			</router-view>
		</template>
	</div>
</template>
